import { useEffect, useState } from 'react'
// React 16 dispatches inside useEffect trigger synchronous re-renders per dispatch.
// Batching prevents the component from re-rendering between addMenu and grandUserAccess.
// Can be removed when upgrading to React 18+ (automatic batching).
import { unstable_batchedUpdates } from 'react-dom'
import { useHistory, useParams } from '../Router'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  restoreBooking,
  addMenu,
  setPunchCardUuids,
} from '../Appointment/store/Actions'
import { grandUserAccess } from '../Settings/Store/Action'
import { setUserData } from '../App/Util'
import { decodeBookingState } from './decode'
import {
  resolveActivities,
  findMenuActivity,
  findMenuCategory,
} from './resolveActivities'

function decodeCustomerInfo(encoded: string): void {
  try {
    const data = JSON.parse(atob(encoded))
    setUserData(data)
  } catch {
    // ignore invalid customer info
  }
}

type IntentResult = 'pending' | 'handled' | 'none'

export function useBookingIntent(): IntentResult {
  const [result, setResult] = useState<IntentResult>('pending')
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { establishmentId } = useParams<{ [key: string]: string }>()

  const { persons, menuCategories, extraActivities } = useAppSelector(
    (state) => ({
      persons: state.persons.persons,
      menuCategories: state.menu.categories,
      extraActivities: state.activities.list,
    })
  )

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const activityId = params.get('activityId')
    const categoryId = params.get('categoryId')
    const restoreParam = params.get('restore')
    const customerInfo = params.get('customerInfo')
    const punchCardId = params.get('punchCardId')

    if (!activityId && !categoryId && !restoreParam) {
      setResult('none')
      return
    }

    if (!menuCategories) return

    // Pre-fill customer form from ?customerInfo= (must run before any dispatches)
    if (customerInfo && (activityId || categoryId)) {
      decodeCustomerInfo(customerInfo)
    }

    // Deep link: ?activityId= or ?categoryId=
    if (categoryId) {
      const menuCategory = findMenuCategory(menuCategories, Number(categoryId))
      if (menuCategory?.activities) {
        unstable_batchedUpdates(() => {
          dispatch(addMenu(menuCategory.activities!, menuCategory.id))
          dispatch(grandUserAccess())
          if (punchCardId) dispatch(setPunchCardUuids([punchCardId]))
        })
        history.replace(`/${establishmentId}/appointment`)
        setResult('handled')
        return
      }
    }

    if (activityId) {
      const activity = findMenuActivity(menuCategories, Number(activityId))
      if (activity && activity.categoryId) {
        unstable_batchedUpdates(() => {
          dispatch(addMenu([activity], activity.categoryId!))
          dispatch(grandUserAccess())
          if (punchCardId) dispatch(setPunchCardUuids([punchCardId]))
        })
        history.replace(`/${establishmentId}/appointment`)
        setResult('handled')
        return
      }
    }

    // Restore: ?restore=
    if (restoreParam) {
      if (!extraActivities) return

      const state = decodeBookingState(restoreParam)
      if (state) {
        const customers = resolveActivities(
          state,
          menuCategories,
          extraActivities,
          persons ?? []
        )

        if (customers) {
          const formData = state.formData
            ? { ...state.formData, acceptTerms: true }
            : undefined

          dispatch(restoreBooking(customers, state.date, formData))
          dispatch(grandUserAccess())
          history.replace(`/${establishmentId}/appointment/confirm`)
          setResult('handled')
          return
        }
      }
    }

    setResult('none')
  }, [menuCategories, extraActivities, establishmentId, persons])

  return result
}
